import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Check, CircleUserRound, Puzzle, Search, X } from 'lucide-react';
import {
    DEFAULT_MODEL_ENTRY,
    FALLBACK_GROUP_LABELS,
    ModelIcon,
} from '../../components/ModelSelector';
import {
    useDevAgents,
    useDevAgentsStatus,
    useDevSkills,
    useDevSkillsStatus,
    useDevModels,
    useDevToolsActions,
} from '../store/hooks';

const DEV_TOOLS_ENABLED = import.meta.env.VITE_DEV_TOOLS === 'true';
const DEV_MODEL_ENABLED = import.meta.env.VITE_DEV_TOOLS_MODEL === 'true';
const DEV_AGENT_ENABLED = import.meta.env.VITE_DEV_TOOLS_AGENT === 'true';
const DEV_SKILL_ENABLED = import.meta.env.VITE_DEV_TOOLS_SKILL === 'true';

export const isDevToolsEnabled = () =>
    DEV_TOOLS_ENABLED && (DEV_MODEL_ENABLED || DEV_AGENT_ENABLED || DEV_SKILL_ENABLED);

export const isDevModelEnabled = () => DEV_TOOLS_ENABLED && DEV_MODEL_ENABLED;
export const isDevAgentEnabled = () => DEV_TOOLS_ENABLED && DEV_AGENT_ENABLED;
export const isDevSkillEnabled = () => DEV_TOOLS_ENABLED && DEV_SKILL_ENABLED;

function Tooltip({ content, children, delay = 80, position = 'top' }) {
    const [show, setShow] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleEnter = () => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setShow(true), delay);
    };
    const handleLeave = () => {
        clearTimeout(timerRef.current);
        setShow(false);
    };

    const placement = position === 'top'
        ? 'bottom-full left-1/2 -translate-x-1/2 mb-1'
        : 'top-full left-1/2 -translate-x-1/2 mt-1';

    return (
        <span
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            className="relative inline-flex"
        >
            {children}
            {show && content && (
                <span
                    role="tooltip"
                    className={`absolute ${placement} whitespace-nowrap px-2 py-1 rounded-md bg-gray-900 text-white text-[11px] shadow-lg z-[60] pointer-events-none`}
                >
                    {content}
                </span>
            )}
        </span>
    );
}

function SlidersIcon({ className = 'w-4 h-4' }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M3 7.375h6.829a2.501 2.501 0 0 0 4.842 0H17a.625.625 0 1 0 0-1.25h-2.329a2.501 2.501 0 0 0-4.842 0H3a.625.625 0 1 0 0 1.25M12.25 5.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5" />
            <path fillRule="evenodd" d="M7.75 15.75a2.5 2.5 0 0 0 2.421-1.875H17a.625.625 0 1 0 0-1.25h-6.829a2.5 2.5 0 0 0-4.842 0H3a.625.625 0 1 0 0 1.25h2.329A2.5 2.5 0 0 0 7.75 15.75m0-1.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5" clipRule="evenodd" />
        </svg>
    );
}

function SectionRow({ renderIcon, label, sublabel, renderSublabel, active, onActivate }) {
    return (
        <button
            type="button"
            onMouseEnter={onActivate}
            onClick={onActivate}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors text-left ${
                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
            <span className="flex-shrink-0 text-gray-500">
                {renderIcon?.('w-4 h-4')}
            </span>
            <span className="flex-shrink-0 font-medium">{label}</span>
            <span className="flex-1 min-w-0 text-right text-[12px] text-gray-400 truncate">
                {renderSublabel ? renderSublabel() : sublabel}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        </button>
    );
}

function ModelIconFromSelection({ selectedModel, className }) {
    const { models } = useModelList();
    const info = models.find((m) => m.value === selectedModel) || models[0];
    return <ModelIcon group={info?.group || 'default'} className={className} />;
}

function SelectedModelLabel({ selectedModel }) {
    const label = useSelectedModelLabel(selectedModel);
    return <>{label}</>;
}

export function ActiveModelChip({ selectedModel, onClear, onClick }) {
    const label = useSelectedModelLabel(selectedModel);
    const { models } = useModelList();
    const info = useMemo(
        () => models.find((m) => m.value === selectedModel),
        [models, selectedModel],
    );
    if (!selectedModel) return null;
    return (
        <Tooltip content={`Model: ${label}`}>
            <div className="inline-flex items-center h-7 pl-2 pr-1 rounded-full bg-blue-50 text-blue-700">
                <button
                    type="button"
                    onClick={onClick}
                    className="flex items-center focus:outline-none"
                    aria-label={`Model: ${label}`}
                >
                    <ModelIcon group={info?.group || 'default'} className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClear?.(); }}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-blue-100 transition-colors"
                    aria-label="Đặt lại model mặc định"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        </Tooltip>
    );
}

function SelectedAgentLabel({ agentId, cachedName }) {
    const agents = useDevAgents();
    const name = useMemo(() => {
        if (!agentId) return 'Mặc định';
        if (cachedName) return cachedName;
        const found = (agents || []).find((a) => a.id === agentId);
        return found?.name || agentId;
    }, [agents, agentId, cachedName]);
    return <>{name}</>;
}

export function ActiveAgentChip({ agentId, cachedName, onClear, onClick, onNameResolved }) {
    const agents = useDevAgents();
    const resolvedFromStore = useMemo(() => {
        const found = (agents || []).find((a) => a.id === agentId);
        return found?.name || null;
    }, [agents, agentId]);

    useEffect(() => {
        if (resolvedFromStore && resolvedFromStore !== cachedName) {
            onNameResolved?.(resolvedFromStore);
        }
    }, [resolvedFromStore, cachedName, onNameResolved]);

    if (!agentId) return null;

    const display = cachedName || resolvedFromStore || agentId;
    return (
        <Tooltip content={`Agent: ${display}`}>
            <div className="inline-flex items-center h-7 pl-2 pr-1 rounded-full bg-blue-50 text-blue-700">
                <button
                    type="button"
                    onClick={onClick}
                    className="flex items-center focus:outline-none"
                    aria-label={`Agent: ${display}`}
                >
                    <CircleUserRound className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClear?.(); }}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-blue-100 transition-colors"
                    aria-label="Bỏ chọn agent"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        </Tooltip>
    );
}

const SubmenuContext = React.createContext({ side: 'right', verticalAlign: 'top', maxHeight: null });

function SubmenuShell({ children }) {
    const { side, verticalAlign, maxHeight } = React.useContext(SubmenuContext);
    const horizontal = side === 'left' ? 'right-full mr-1' : 'left-full ml-1';
    const vertical = verticalAlign === 'bottom' ? 'bottom-0' : 'top-0';
    return (
        <div
            className={`absolute ${horizontal} ${vertical} w-72 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden flex flex-col`}
            style={maxHeight ? { maxHeight } : undefined}
        >
            <div className="py-1.5 flex-1 min-h-0 overflow-y-auto auto-hide-scrollbar">{children}</div>
        </div>
    );
}

function useModelList() {
    const remote = useDevModels();
    const { fetchModels } = useDevToolsActions();
    useEffect(() => { fetchModels(); }, [fetchModels]);
    const models = useMemo(
        () => (remote && remote.length > 0 ? [DEFAULT_MODEL_ENTRY, ...remote] : [DEFAULT_MODEL_ENTRY]),
        [remote],
    );
    const groups = useMemo(() => {
        const seen = new Map();
        const result = [];
        for (const m of models) {
            if (!seen.has(m.group)) {
                const label = m.group_label
                    || FALLBACK_GROUP_LABELS[m.group]
                    || m.group.charAt(0).toUpperCase() + m.group.slice(1);
                seen.set(m.group, label);
                result.push({ key: m.group, label });
            }
        }
        return result;
    }, [models]);
    return { models, groups };
}

function ModelSubmenu({ selectedModel, onModelChange }) {
    const { models, groups } = useModelList();
    const [query, setQuery] = useState('');
    const filteredModels = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return models;
        return models.filter((m) => {
            const groupLabel = FALLBACK_GROUP_LABELS[m.group] || m.group || '';
            return (
                String(m.label || '').toLowerCase().includes(q)
                || String(m.value || '').toLowerCase().includes(q)
                || groupLabel.toLowerCase().includes(q)
            );
        });
    }, [models, query]);
    const hasResults = filteredModels.length > 0;

    return (
        <SubmenuShell>
            <SearchBox value={query} onChange={setQuery} placeholder="Tìm kiếm Model" />
            {!hasResults && (
                <div className="px-4 py-3 text-xs text-gray-400">Không tìm thấy model</div>
            )}
            {hasResults && (
                <div>
                    {groups.map((group) => {
                        const groupModels = filteredModels.filter((m) => m.group === group.key);
                        if (groupModels.length === 0) return null;
                        return (
                            <div key={group.key} className="px-1.5">
                                {group.key !== 'default' && (
                                    <div className="px-2.5 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                        {group.label}
                                    </div>
                                )}
                                {groupModels.map((model) => {
                                    const isActive = selectedModel === model.value || (!selectedModel && model.value === '');
                                    return (
                                        <button
                                            key={model.value}
                                            type="button"
                                            onClick={() => onModelChange?.(model.value)}
                                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors text-left ${
                                                isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            <ModelIcon group={model.group} className="w-4 h-4 flex-shrink-0" />
                                            <span className="flex-1 truncate">{model.label}</span>
                                            {isActive && <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </SubmenuShell>
    );
}

function useSelectedModelLabel(selectedModel) {
    const { models } = useModelList();
    return (models.find((m) => m.value === selectedModel) || models[0])?.label || 'Auto';
}

function AgentSubmenu({ selectedAgentId, onChange, onClear }) {
    const rawItems = useDevAgents();
    const status = useDevAgentsStatus();
    const { fetchAgents } = useDevToolsActions();
    useEffect(() => { fetchAgents(); }, [fetchAgents]);
    const items = useMemo(
        () => (rawItems || []).map((a) => ({ id: a.id, label: a.name || a.id, description: a.description })),
        [rawItems],
    );
    const loading = status === 'loading' && items.length === 0;
    const error = status === 'failed' ? 'Không tải được agent' : null;
    const [query, setQuery] = useState('');
    const filtered = useMemo(
        () => filterByQuery(items, query, ['label', 'id', 'description']),
        [items, query],
    );

    return (
        <SubmenuShell>
            <SearchBox value={query} onChange={setQuery} placeholder="Tìm kiếm Agent" />
            {selectedAgentId && (
                <div className="px-1.5 mb-1">
                    <button
                        type="button"
                        onClick={onClear}
                        className="w-full px-2.5 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-50 text-left flex items-center gap-2"
                    >
                        <span className="truncate flex-1">Bỏ chọn agent</span>
                        <span className="text-gray-400 font-mono text-[10px] truncate">{selectedAgentId}</span>
                    </button>
                </div>
            )}
            {loading && <div className="px-4 py-3 text-xs text-gray-400">Đang tải...</div>}
            {error && <div className="px-4 py-3 text-xs text-red-500">{error}</div>}
            {!loading && !error && (
                <ListPanel
                    items={filtered}
                    selectedId={selectedAgentId}
                    onSelect={(it) => (it.id === selectedAgentId ? onClear() : onChange({ id: it.id, name: it.label }))}
                    emptyLabel="Không có agent"
                    renderItem={(it) => (
                        <div className="flex items-start gap-2 w-full min-w-0">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{it.label}</div>
                                {it.description && (
                                    <div className="text-[11px] text-gray-500 truncate mt-0.5">{it.description}</div>
                                )}
                            </div>
                            {selectedAgentId === it.id && (
                                <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                            )}
                        </div>
                    )}
                />
            )}
        </SubmenuShell>
    );
}

function SkillSubmenu({ onPick }) {
    const rawItems = useDevSkills();
    const status = useDevSkillsStatus();
    const { fetchSkills } = useDevToolsActions();
    useEffect(() => { fetchSkills(); }, [fetchSkills]);
    const items = useMemo(
        () => (rawItems || []).map((s) => ({
            id: s.id,
            label: s.name || s.id,
            name: s.name || s.id,
            description: s.description,
        })),
        [rawItems],
    );
    const loading = status === 'loading' && items.length === 0;
    const error = status === 'failed' ? 'Không tải được skill' : null;
    const [query, setQuery] = useState('');
    const filtered = useMemo(
        () => filterByQuery(items, query, ['label', 'name', 'description']),
        [items, query],
    );

    return (
        <SubmenuShell>
            <SearchBox value={query} onChange={setQuery} placeholder="Tìm kiếm Kỹ năng" />
            {loading && <div className="px-4 py-3 text-xs text-gray-400">Đang tải...</div>}
            {error && <div className="px-4 py-3 text-xs text-red-500">{error}</div>}
            {!loading && !error && (
                <ListPanel
                    items={filtered}
                    onSelect={(it) => onPick(it.name)}
                    emptyLabel="Không có skill"
                    renderItem={(it) => (
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{it.label}</div>
                            {it.description && (
                                <div className="text-[11px] text-gray-500 truncate mt-0.5">{it.description}</div>
                            )}
                        </div>
                    )}
                />
            )}
        </SubmenuShell>
    );
}

function ListPanel({ items, emptyLabel, selectedId, onSelect, renderItem }) {
    if (!items || items.length === 0) {
        return <div className="px-4 py-3 text-xs text-gray-400">{emptyLabel}</div>;
    }
    return (
        <div className="px-1.5">
            {items.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item)}
                    className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm hover:bg-gray-50 transition-colors flex items-center justify-between gap-2 ${
                        selectedId === item.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                >
                    {renderItem ? renderItem(item) : <span className="truncate">{item.label}</span>}
                </button>
            ))}
        </div>
    );
}

function SearchBox({ value, onChange, placeholder }) {
    return (
        <div className="px-1.5 pb-1.5">
            <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    type="text"
                    autoFocus
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full pl-8 pr-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 placeholder:text-gray-400"
                />
            </div>
        </div>
    );
}

function filterByQuery(items, query, fields = ['label']) {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
        fields.some((f) => String(it[f] || '').toLowerCase().includes(q)),
    );
}

export default function DevToolsMenu({
    selectedModel,
    onModelChange,
    disabled = false,
    agentId = null,
    agentName = null,
    onAgentChange,
    onClearAgent,
    onSkillInsert,
    onAgentNameHydrate,
}) {
    const [open, setOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const btnRef = useRef(null);
    const popRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (popRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            setOpen(false);
            setActiveSection(null);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (!isDevToolsEnabled()) return null;

    const sections = [
        isDevModelEnabled() && {
            key: 'model',
            renderIcon: (cls) => <ModelIconFromSelection selectedModel={selectedModel} className={cls} />,
            label: 'Model',
            renderSublabel: () => <SelectedModelLabel selectedModel={selectedModel} />,
        },
        isDevAgentEnabled() && {
            key: 'agent',
            renderIcon: (cls) => <CircleUserRound className={cls} />,
            label: 'Agent',
            renderSublabel: () => (agentId ? <SelectedAgentLabel agentId={agentId} cachedName={agentName} /> : 'Mặc định'),
        },
        isDevSkillEnabled() && {
            key: 'skill',
            renderIcon: (cls) => <Puzzle className={cls} />,
            label: 'Skill',
            sublabel: 'Chèn /skill vào input',
        },
    ].filter(Boolean);

    const openAgentSection = () => { setOpen(true); setActiveSection('agent'); };
    const openModelSection = () => { setOpen(true); setActiveSection('model'); };

    const [popoverPlacement, setPopoverPlacement] = useState(null);
    useLayoutEffect(() => {
        if (!open || !btnRef.current) return;
        const compute = () => {
            const r = btnRef.current.getBoundingClientRect();
            const MAIN_W = 288;
            const SUBMENU_W = 288;
            const GAP = 4;
            const PAD = 8;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            let left = r.left;
            if (left + MAIN_W > vw - PAD) left = Math.max(PAD, vw - MAIN_W - PAD);
            if (left < PAD) left = PAD;
            const submenuFitsRight = left + MAIN_W + GAP + SUBMENU_W <= vw - PAD;
            const submenuFitsLeft = left - GAP - SUBMENU_W >= PAD;
            const submenuSide = submenuFitsRight ? 'right' : (submenuFitsLeft ? 'left' : 'right');
            const spaceAbove = r.top - PAD;
            const spaceBelow = vh - r.bottom - PAD;
            const placeAbove = spaceAbove >= 200 || spaceAbove >= spaceBelow;
            const style = placeAbove
                ? { left, bottom: vh - r.top + 8 }
                : { left, top: r.bottom + 8 };
            const mainHeightEstimate = Math.min(320, 36 * sections.length + 24);
            const mainTop = placeAbove ? r.top - 8 - mainHeightEstimate : r.bottom + 8;
            const mainBottom = mainTop + mainHeightEstimate;
            const spaceDownFromMainTop = vh - PAD - mainTop;
            const spaceUpFromMainBottom = mainBottom - PAD;
            const submenuAlignBottom = spaceUpFromMainBottom > spaceDownFromMainTop;
            const SUBMENU_MAX_HEIGHT_CAP = 420;
            const availableVertical = submenuAlignBottom ? spaceUpFromMainBottom : spaceDownFromMainTop;
            const submenuMaxHeight = Math.min(
                SUBMENU_MAX_HEIGHT_CAP,
                Math.max(160, availableVertical),
            );
            setPopoverPlacement({
                style,
                submenuSide,
                submenuVerticalAlign: submenuAlignBottom ? 'bottom' : 'top',
                submenuMaxHeight,
            });
        };
        compute();
        window.addEventListener('resize', compute);
        window.addEventListener('scroll', compute, true);
        return () => {
            window.removeEventListener('resize', compute);
            window.removeEventListener('scroll', compute, true);
        };
    }, [open, sections.length]);

    return (
        <div className="relative flex items-center gap-1.5">
            <button
                ref={btnRef}
                onClick={() => { setOpen((v) => !v); setActiveSection(null); }}
                type="button"
                disabled={disabled}
                title="Dev tools"
                className={`w-7 h-7 flex items-center justify-center rounded-full border text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 ${
                    agentId ? 'border-blue-400 text-blue-600' : 'border-gray-200'
                }`}
            >
                <SlidersIcon className="w-3.5 h-3.5" />
            </button>
            {isDevModelEnabled() && selectedModel && (
                <ActiveModelChip
                    selectedModel={selectedModel}
                    onClear={() => onModelChange?.('')}
                    onClick={openModelSection}
                />
            )}
            {isDevAgentEnabled() && agentId && (
                <ActiveAgentChip
                    agentId={agentId}
                    cachedName={agentName}
                    onClear={() => onClearAgent?.()}
                    onClick={openAgentSection}
                    onNameResolved={onAgentNameHydrate}
                />
            )}

            {open && popoverPlacement && createPortal(
                <SubmenuContext.Provider value={{ side: popoverPlacement.submenuSide, verticalAlign: popoverPlacement.submenuVerticalAlign, maxHeight: popoverPlacement.submenuMaxHeight }}>
                <div
                    ref={popRef}
                    className="fixed z-[70]"
                    style={popoverPlacement.style}
                >
                    <div className="relative">
                        <div className="w-72 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden p-1.5">
                            {sections.map((s) => (
                                <SectionRow
                                    key={s.key}
                                    renderIcon={s.renderIcon}
                                    label={s.label}
                                    sublabel={s.sublabel}
                                    renderSublabel={s.renderSublabel}
                                    active={activeSection === s.key}
                                    onActivate={() => setActiveSection(s.key)}
                                />
                            ))}
                        </div>

                        {activeSection === 'model' && isDevModelEnabled() && (
                            <ModelSubmenu
                                selectedModel={selectedModel}
                                onModelChange={(m) => { onModelChange?.(m); }}
                            />
                        )}
                        {activeSection === 'agent' && isDevAgentEnabled() && (
                            <AgentSubmenu
                                selectedAgentId={agentId}
                                onChange={(id) => { onAgentChange?.(id); setOpen(false); setActiveSection(null); }}
                                onClear={() => { onClearAgent?.(); setOpen(false); setActiveSection(null); }}
                            />
                        )}
                        {activeSection === 'skill' && isDevSkillEnabled() && (
                            <SkillSubmenu
                                onPick={(name) => { onSkillInsert?.(name); setOpen(false); setActiveSection(null); }}
                            />
                        )}
                    </div>
                </div>
                </SubmenuContext.Provider>,
                document.body,
            )}
        </div>
    );
}
